import { useId } from 'react';
import styled from 'styled-components';
import { LogoTriad } from '@/icons/brand/LogoTriad';

const Art = styled.div`
  position: relative;
  width: 100%;
  height: 180px;
  align-self: end;
  color: var(--banner-icon);
  & > svg {
    width: 100%;
    height: 100%;
    overflow: visible;
  }
`;
const BrandArt = styled(Art)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  text-align: center;
  & > svg {
    width: 98px;
    height: 98px;
    filter: drop-shadow(0 12px 28px #8b5cf640);
  }
  & p {
    margin: 0;
    font:
      italic 19px/1.4 Georgia,
      serif;
    transform: rotate(-7deg);
  }
  & p::after {
    content: '';
    display: block;
    margin: 8px auto 0;
    width: 90px;
    height: 1px;
    background: #9d74ee;
  }
  @media (max-width: 520px) {
    gap: 7px;
    & > svg {
      width: 60px;
      height: 60px;
    }
    & p {
      font-size: 10px;
    }
    & p::after {
      width: 40px;
      margin-top: 3px;
    }
  }
`;

export function BannerArtwork({ variant, version }: { variant: number; version: string }) {
  const id = useId().replace(/:/g, '');
  const gradient = `${id}-card`;
  const glow = `${id}-glow`;
  if (variant === 1)
    return (
      <BrandArt aria-hidden="true">
        <LogoTriad size={98} />
        <p>
          Real People
          <br />
          Real Conversations
        </p>
      </BrandArt>
    );
  return (
    <Art aria-hidden="true">
      <svg viewBox="0 0 270 220" fill="none">
        <defs>
          <linearGradient
            id={gradient}
            x1="0"
            y1="0"
            x2=".8"
            y2="1"
            gradientUnits="objectBoundingBox"
          >
            <stop stopColor="var(--banner-art-from)" />
            <stop offset=".45" stopColor="var(--banner-art-mid)" />
            <stop offset="1" stopColor="var(--banner-art-to)" />
          </linearGradient>
          <radialGradient id={glow}>
            <stop stopColor="#9f61fa" stopOpacity=".28" />
            <stop offset="1" stopColor="#9f61fa" stopOpacity="0" />
          </radialGradient>
        </defs>
        <ellipse cx="155" cy="140" rx="130" ry="110" fill={`url(#${glow})`} />
        {variant === 0 ? (
          <>
            <g transform="translate(132 18) skewY(13)">
              <rect
                width="107"
                height="149"
                rx="7"
                fill={`url(#${gradient})`}
                stroke="#aa78f5"
                strokeOpacity=".55"
              />
              <path
                d="M14 22h72M14 37h49M14 84h74M14 96h60"
                stroke="#a588ec"
                strokeWidth="5"
                opacity=".3"
                strokeLinecap="round"
              />
              <rect x="14" y="50" width="75" height="19" rx="3" fill="#9d72e6" opacity=".1" />
            </g>
            <g transform="translate(82 46) skewY(13)">
              <rect
                width="107"
                height="153"
                rx="7"
                fill={`url(#${gradient})`}
                stroke="#8352d6"
                strokeOpacity=".6"
              />
              <path
                d="M14 20h66M14 36h44M14 115h65M14 128h45"
                stroke="#ae8ded"
                strokeWidth="5"
                opacity=".25"
                strokeLinecap="round"
              />
            </g>
            <g transform="translate(64 67) skewY(13)">
              <rect
                width="127"
                height="145"
                rx="8"
                fill={`url(#${gradient})`}
                stroke="#9c6df2"
                strokeOpacity=".8"
              />
              <text
                x="14"
                y="36"
                fill="var(--banner-art-text)"
                fontSize={version.length > 9 ? 16 : 21}
                fontWeight="600"
              >
                v{version}
              </text>
              <path
                d="M17 60h91M17 77h68M17 94h82M17 111h52"
                stroke="#a98bea"
                strokeWidth="6"
                opacity=".55"
                strokeLinecap="round"
              />
            </g>
            <g transform="translate(22 105) skewY(13)">
              <rect width="38" height="92" rx="5" fill={`url(#${gradient})`} stroke="#8450d7" />
              <rect x="9" y="15" width="21" height="25" rx="4" fill="#9d50ff" />
              <path
                d="M14 23h11m-11 6h11M10 54h18m-18 12h13"
                stroke="#dbc5ff"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </g>
            <circle cx="215" cy="174" r="25" fill="#7637de" />
            <circle cx="215" cy="174" r="24" stroke="#b683ff" strokeOpacity=".5" />
            <path
              d="M215 186v-25m-9 9 9-9 9 9"
              stroke="#eadcff"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="m248 65 7 4v8l-7 4-7-4v-8Zm-5 31 5 3v6l-5 3-5-3v-6ZM47 57l5 3v6l-5 3-5-3v-6Z"
              fill="#9253e9"
              opacity=".6"
            />
          </>
        ) : variant === 2 ? (
          <>
            <circle cx="210" cy="49" r="35" fill="#8b5cf6" opacity=".14" />
            <path
              d="M0 160 69 114l39 21 59-58 50 14 53-31v160H0Z"
              fill="var(--banner-mountain-back)"
            />
            <path
              d="m0 192 76-51 35 7 67-63 55 15 37-15v135H0Z"
              fill="var(--banner-mountain-front)"
            />
            <path
              d="M81 222c134-42 3-38 83-75 66-31-3-45 43-84"
              stroke="var(--banner-path)"
              strokeWidth="12"
            />
            <path
              d="M81 222c134-42 3-38 83-75 66-31-3-45 43-84"
              stroke="#a482e1"
              strokeDasharray="3 6"
              strokeOpacity=".45"
            />
            <path
              d="M207 69V33m0 2c8-7 13 6 22 0v17c-9 7-14-6-22 0"
              stroke="#b781ff"
              strokeWidth="2"
              fill="#8643da"
            />
            <text
              x="113"
              y="170"
              fill="var(--banner-link)"
              fontSize="16"
              fontStyle="italic"
              transform="rotate(-9 113 170)"
            >
              <tspan x="113">A more open,</tspan>
              <tspan x="101" dy="23">
                connected tomorrow
              </tspan>
            </text>
          </>
        ) : (
          <>
            <g transform="translate(96 104) rotate(8)">
              <path
                d="M0 8a8 8 0 0 1 8-8h134a8 8 0 0 1 8 8v64a8 8 0 0 1-8 8h-16v19l-23-19H8a8 8 0 0 1-8-8Z"
                fill="var(--banner-mountain-back)"
              />
              <path
                d="M27 23h94M27 39h70"
                stroke="#8d5ccb"
                strokeWidth="4"
                opacity=".4"
                strokeLinecap="round"
              />
            </g>
            <g transform="translate(40 55) rotate(-7)">
              <path
                d="M0 10A10 10 0 0 1 10 0h161a10 10 0 0 1 10 10v76a10 10 0 0 1-10 10H58l-27 23V96H10A10 10 0 0 1 0 86Z"
                fill={`url(#${gradient})`}
                stroke="#9264db"
                strokeOpacity=".6"
              />
              <text x="89" y="39" textAnchor="middle" fill="var(--banner-art-text)" fontSize="16">
                <tspan x="89">Your ideas</tspan>
                <tspan x="89" dy="24">
                  shape TruXSocial
                </tspan>
              </text>
            </g>
            <path
              d="m35 48-8-10m24 4-3-13m-21 31-12-4m217 83 12 4m-17 7 3 13m-6-42 11-5"
              stroke="#8251c6"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path d="m221 166 7 7-6 9" stroke="#ab6bea" strokeWidth="2" strokeLinecap="round" />
          </>
        )}
      </svg>
    </Art>
  );
}
